import { useState } from "react";
import { Building2, Check, ExternalLink, Loader2, Plus, Search } from "lucide-react";
import { Input } from "@/frontend/components/ui/input";
import { cn } from "@/frontend/lib/utils";
import { logoUrl } from "@/frontend/utils/shared";
import { useFhirSourcesSearchQuery } from "@/frontend/queries/dashboard/patients";
import { useWorkspacePersona } from "../individual/useWorkspacePersona";
import { healthSystemSearchCopy } from "@/frontend/utils/dashboard/connections";
import {
  ROW_ACTION_COPY,
  type HealthSystemOption,
  type HealthSystemRowAction,
} from "@/frontend/types/dashboard/health-systems";

/** Logo tile shared by the result rows and the chips. */
function SourceLogo({ logo, size = "sm" }: { logo: string | null; size?: "sm" | "xs" }) {
  const url = logoUrl(logo);
  const box = size === "sm" ? "size-8" : "size-6";
  const img = size === "sm" ? "size-5" : "size-4";

  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-md border bg-background",
        box,
      )}
    >
      {url ? (
        <img
          src={url}
          alt=""
          className={cn("object-contain", img)}
          data-ph-no-capture
          onError={(event) => {
            event.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <Building2 className="size-3.5 text-muted-foreground" />
      )}
    </span>
  );
}

/**
 * One selectable health system with its logo, name, type and add affordance.
 * Shared by the search results and the default suggestions so both read
 * identically. The name wraps in full rather than truncating, since two systems
 * sharing a long prefix would ellipsize to the same string.
 */
export function HealthSystemOptionRow({
  source,
  added,
  onSelect,
  action = "add",
}: {
  source: HealthSystemOption;
  added: boolean;
  onSelect: () => void;
  /** Defaults to `add`. */
  action?: HealthSystemRowAction;
}) {
  const copy = ROW_ACTION_COPY[action];

  return (
    <button
      type="button"
      disabled={added}
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors",
        added ? "cursor-default bg-emerald-50 dark:bg-emerald-500/10" : "hover:bg-accent/50",
      )}
    >
      <SourceLogo logo={source.logo} />
      <span className="min-w-0 flex-1">
        <span className="block break-words text-body font-medium leading-snug" data-patient>
          {source.name}
        </span>
        {source.type && (
          <span className="block break-words text-caption capitalize text-muted-foreground">
            {source.type}
          </span>
        )}
      </span>
      {added ? (
        <span className="flex shrink-0 items-center gap-1 text-caption font-medium text-emerald-600 dark:text-emerald-400">
          <Check className="size-3.5" />
          {copy.settled}
        </span>
      ) : copy.pending ? (
        <span className="flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-primary px-2.5 text-caption font-medium text-primary-foreground">
          {copy.pending}
          <ExternalLink className="size-3.5" />
        </span>
      ) : (
        <Plus className="size-4 shrink-0 text-muted-foreground" />
      )}
    </button>
  );
}

/**
 * The workspace-wide health system search, used when adding a patient and when
 * editing their suggestions later. Already-added systems stay in the results as
 * a ticked row, because hiding them made a search for an added system report no
 * matches, which reads as a broken search.
 */
export function HealthSystemSearch({
  addedUrls,
  onSelect,
  placeholder,
  action = "add",
}: {
  addedUrls: Set<string>;
  onSelect: (option: HealthSystemOption) => void;
  /** Overrides the environment-derived placeholder. */
  placeholder?: string;
  /** What picking a result means. Defaults to `add`. */
  action?: HealthSystemRowAction;
}) {
  const [query, setQuery] = useState("");
  const [dismissed, setDismissed] = useState(false);
  const { data: results, isLoading } = useFhirSourcesSearchQuery(query);
  // The server scopes results to the workspace's environment, so the copy has to
  // say which one the reader is searching.
  const { environment } = useWorkspacePersona();
  const searchCopy = healthSystemSearchCopy(environment);

  const options = results ?? [];
  const showResults = query.trim().length >= 2 && !dismissed;

  /**
   * Closes the result list on any pointer press outside this control, so
   * clicking elsewhere in the panel dismisses it. A ref callback rather than an
   * effect; the returned cleanup detaches the listener on unmount.
   */
  const dismissOnOutsidePress = (node: HTMLDivElement | null) => {
    if (!node) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!node.contains(event.target as Node)) setDismissed(true);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  };

  return (
    <div ref={dismissOnOutsidePress}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setDismissed(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setDismissed(true);
          }}
          placeholder={placeholder ?? searchCopy.placeholder}
          className="h-9 pl-9 text-body"
        />
      </div>

      {showResults && (
        // Fixed height, not max-height: a shrinking result list must not resize
        // the panel around it while the user is still typing.
        <div className="mt-2 h-64 overflow-y-auto rounded-lg border">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="size-4 animate-spin text-muted-foreground" />
            </div>
          ) : options.length === 0 ? (
            <p className="flex h-full items-center justify-center px-4 text-center text-body text-muted-foreground">
              {searchCopy.emptyFor(query.trim())}
            </p>
          ) : (
            <div className="divide-y">
              {options.map((source) => (
                <HealthSystemOptionRow
                  key={source.fhirBaseUrl}
                  source={source}
                  action={action}
                  added={addedUrls.has(source.fhirBaseUrl)}
                  onSelect={() => {
                    onSelect(source);
                    setQuery("");
                    setDismissed(false);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Removable chip for a chosen health system. Tinted green so a saved suggestion
 * reads as confirmed rather than as another empty input. The name wraps in full
 * rather than truncating, so two systems sharing a long prefix stay tellable
 * apart.
 */
export function HealthSystemChip({
  name,
  logo,
  onRemove,
  disabled,
}: {
  name: string;
  logo: string | null;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-50 py-1.5 pl-1.5 pr-1 dark:bg-emerald-500/10">
      <SourceLogo logo={logo} size="xs" />
      <Check className="size-3 shrink-0 text-emerald-600 dark:text-emerald-400" />
      <span className="max-w-[18rem] break-words text-body font-medium leading-snug" data-patient>
        {name}
      </span>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label={`Remove ${name}`}
        className="flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-emerald-500/15 hover:text-foreground disabled:opacity-40"
      >
        <span aria-hidden>×</span>
      </button>
    </span>
  );
}
